import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';

import { useTranslation } from 'react-i18next';

import 'translations/translations';
import { ContactDto, UserModelDto } from '.generated/api';
import { ValidatedTextInput } from 'components/ValidatedTextInput';
import { organizationActions } from 'actions/organizationActions';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UserAutocomplete } from 'features/common/UserAutocomplete';
import clsx from 'clsx';
import OverlaySpinner from 'components/spinners/OverlaySpinner';

type DialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  contact?: ContactDto;
};

interface ParamTypes {
  orgId: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0.1rem 1rem',
    minWidth: '8.5rem',
  },
  alignRight: {
    textAlign: 'right',
  },
  noMarginBottom: {
    marginBottom: 0,
  },
  lastButton: {
    marginRight: 0,
    marginBottom: 0,
  },
  dialogContentHeigh: {
    height: '15rem',
  },
  dialogContentAuto: {
    height: 'auto',
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

type Contact = {
  name?: string;
  email?: string;
  phone?: string;
};

const CreateEditContactDialog: React.FC<DialogProps> = (props) => {
  const { isOpen, handleClose, contact } = props;
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgId } = useParams<ParamTypes>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [newContact, setNewContact] = useState<Contact>({
    name: '',
    email: '',
    phone: '',
  });
  const [error, setError] = useState<string>();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;
    setNewContact((newContact) => ({ ...newContact, [name]: value }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEmailChange = (name: string, value: string, _error: boolean) => {
    setNewContact((newContact) => ({ ...newContact, [name]: value }));
  };

  const handleChangeName = (name: string) => {
    setNewContact((newContact) => ({ ...newContact, name }));
  };

  const addOrEditCallback = (error?: string) => {
    setIsLoading(false);
    if (error) {
      setError(error);
      return;
    }
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setIsButtonDisabled(true);
    setNewContact({
      name: '',
      email: '',
      phone: '',
    });
    setError(undefined);

    handleClose();
  };

  const handleCreate = () => {
    setIsLoading(true);
    setError(undefined);
    if (contact) {
      dispatch(
        organizationActions.editContact(
          orgId,
          contact.id,
          newContact.name,
          newContact.email,
          newContact.phone,
          addOrEditCallback,
        ),
      );
    } else {
      dispatch(
        organizationActions.addContact(
          orgId,
          newContact.name,
          newContact.email,
          newContact.phone,
          addOrEditCallback,
        ),
      );
    }
  };

  const setExistingUser = (user: UserModelDto) => {
    const contact: Contact = {
      email: user.userEmail,
      name: user.name,
      phone: user.phone,
    };
    setNewContact(contact);
    handleEmailChange('email', contact.email ?? '', false);
  };

  useEffect(() => {
    if (contact) {
      setNewContact({
        name: contact.name ?? undefined,
        email: contact.email ?? undefined,
        phone: contact.phone ?? undefined,
      });
      setError(undefined);
    }
  }, [contact]);

  useEffect(() => {
    if (!!newContact.name || !!newContact.email || !!newContact.phone) {
      // we have at least one entered value
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [newContact]);

  return (
    <Dialog
      disableBackdropClick={false}
      disableEscapeKeyDown={false}
      maxWidth="md"
      open={isOpen}
      onClose={handleCloseDialog}
    >
      <OverlaySpinner isLoading={isLoading}>
        <DialogTitle disableTypography={true}>
          <Typography variant="h3">
            {contact
              ? t('organization_page.contacts.dialog.title_edit')
              : t('organization_page.contacts.dialog.title_create')}
          </Typography>
        </DialogTitle>

        <DialogContent
          className={clsx(
            !contact ? classes.dialogContentHeigh : classes.dialogContentAuto,
          )}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {contact ? (
                <TextField
                  type="text"
                  variant="outlined"
                  margin="none"
                  fullWidth
                  label={t('organization_page.contacts.labels.contact_name')}
                  name="name"
                  value={newContact.name}
                  onChange={handleChange}
                />
              ) : (
                <UserAutocomplete
                  onSelectOption={setExistingUser}
                  onChangeName={handleChangeName}
                  label={t('organization_page.contacts.labels.contact_name')}
                  value={newContact.name ?? ''}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                variant="outlined"
                margin="none"
                fullWidth
                label={t('organization_page.contacts.labels.contact_phone')}
                name="phone"
                value={newContact.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <ValidatedTextInput
                type="email"
                required={false}
                label={t('organization_page.contacts.labels.contact_email')}
                handleChangeCallback={handleEmailChange}
                inputName="email"
                defaultValue={newContact.email}
                autoFocus={false}
                margin="none"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Typography className={classes.error}>{error}</Typography>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            className={classes.noMarginBottom}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={isButtonDisabled}
            className={classes.lastButton}
          >
            {contact ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </OverlaySpinner>
    </Dialog>
  );
};

export { CreateEditContactDialog };
